import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  searchReportingFetch as fetch,
  searchReportingLegacyUrl,
} from '../../lib/flags/search-reporting';
import { getAuthToken } from '../../utils/auth';
import { editDonationHeaders } from '../../utils/editDonationRequest';
import ReactTooltip from 'react-tooltip';
import uuid from 'react-uuid';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { parsePaymentError } from '@charitystack/shared';
import EventLayout from '../payment-types/EventLayout';
import DeletePaymentModal from '../modals/DeletePaymentModal';
import CancelEventAttendanceModal from '../modals/CancelEventAttendanceModal';
import { calculateDashboardPaymentFees } from '../../utils/dashboardFeeCalculation';
import { useLatestCallback } from '../../hooks/useLatestCallback';
import { getRecordStatusInfo } from '@/components/workspaces/WorkspaceStatusSection';
import {
  EntityIdentityHeader,
  HeaderCloseAction,
  HEADER_ACTION_BUTTON_CLASS,
  WorkspaceSectionCard,
} from '../../../components/workspaces/EntityWorkspacePrimitives';
import { ReceiptPercentIcon } from '@heroicons/react/24/outline';
import TaxDeductibleCard from '../payment-types/form-sections/TaxDeductibleCard';
import type {
  CreateEventPaymentDrawerProps,
  EventFormData,
  FormStep,
  Ticket,
  FinixForm,
  StripeCardRef,
  FinixCardRef,
  FinixBankRef,
  CustomInput,
  EventData,
  TicketInfo,
  Contact,
  WorkspaceHeaderAction,
} from './types/EventPaymentTypes';
import type { Merchant } from '../../types/common';
import {
  buildDashboardEventPurchase,
  buildDashboardInventoryCredits,
  calculateDashboardTicketRowSubtotal,
  resolveDashboardTicketContractVersion,
} from '../../utils/eventTicketPurchase';
// @ts-expect-error Legacy JavaScript utility has no declaration file yet.
import { canSoftDeletePayment, softDeletePayment } from '../../utils/softDeletePayment';
import { canCancelEventAttendance } from '../../utils/cancelEventAttendance';
import {
  formatOfflinePaymentMethod,
  isOfflinePaymentRecord,
} from '../../utils/offlinePaymentMethods';

// Helper function to get the merchant's timezone or fallback to browser timezone
const getTimezone = (merchant: Merchant | null): string => {
  return merchant?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const initialFormData: EventFormData = {
  // Payment fields
  amount: 0,
  fees: 0,
  customFee: 0,
  feeCovered: true,
  paymentMethod: 'Card',
  paymentType: 'Online',
  accountNumber: '',
  routingNumber: '',
  accountType: 'CHECKING',
  anonymous: false,
  cardToken: null,
  // Contact fields
  contactID: '',
  contactSearch: '',
  firstName: '',
  lastName: '',
  organizationName: '',
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  region: '',
  zip: '',
  country: '',
  // Events use tickets, not funds
  funds: [],
  tickets: [
    {
      event: '',
      ticketType: '',
      ticketID: '',
      ticketPrice: 0,
      seats: 1,
      quantity: 1,
      attendees: [],
    },
  ],
  selectedEvent: '',
  selectedEventData: null,
  ticketTotal: 0,
  formName: '',
  // Custom inputs
  globalCustomInputs: [],
  purchaserCustomInputs: [],
  // Promo code fields - now supports multiple codes
  promoCode: '', // Current input value
  appliedPromoCodes: [], // Array of {code, discount, discountType}
  totalPromoDiscount: 0,
  originalTicketTotal: 0,
  // Tax deductible - default to true
  taxDeductible: true,
  // Communication consent
  communicationConsent: false,
};

export default function CreateEventPaymentDrawer(
  props: CreateEventPaymentDrawerProps,
): React.ReactElement {
  // Derive payment processor locally — never mutate props.merchant.
  // Defaults to STRIPE (all new orgs are Stripe; FINIX is legacy).
  const effectivePaymentProcessor = props.merchant?.defaultPaymentProcessor || 'STRIPE';

  const isTaxExempt = props.merchant?.isTaxExempt !== false;
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<FormStep>('form_information');

  // Add edit mode detection
  const isEditMode = !!props.transaction;

  // Add merchant state
  const stripeCardRef = useRef<StripeCardRef>(null);
  const finixCardRef = useRef<FinixCardRef>(null);
  const finixBankRef = useRef<FinixBankRef>(null);
  const [form, setForm] = useState<FinixForm | null>(null);
  const [tokenID, setTokenID] = useState<string>('');
  const [formData, setFormData] = useState<EventFormData>({
    ...initialFormData,
    taxDeductible: isTaxExempt,
  });

  // Generic handler to update any formData field(s)
  const handleFormDataChange = useCallback(
    (updates: Partial<EventFormData>) => {
      setFormData((prev) => ({ ...prev, ...updates }));
    },
    [setFormData],
  );
  // Add state for tracking invalid fields
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  // Add new state for tracking submission
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showCancelAttendanceModal, setShowCancelAttendanceModal] = useState<boolean>(false);
  // Promo code UI state
  const [isPromoProcessing, setIsPromoProcessing] = useState<boolean>(false);
  const [promoMessage, setPromoMessage] = useState<string>('Apply');
  // Contact selection state - moved from EventLayout for validation access
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isNewContact, setIsNewContact] = useState<boolean>(false);

  // Fetch contact data eagerly when drawer opens in edit mode
  // This ensures validation passes even if user doesn't visit Contact Information step
  useEffect(() => {
    if (
      !props.open ||
      !isEditMode ||
      !props.transaction?.contactID ||
      !props.merchant?.merchantName
    ) {
      return;
    }

    // Skip if we already have this contact loaded
    if (selectedContact?.id === props.transaction.contactID) {
      return;
    }

    const fetchContact = async () => {
      // Store transaction locally for type safety in async function
      const transaction = props.transaction;
      if (!transaction?.contactID) return;

      try {
        const contactResponse = await fetch(
          `${searchReportingLegacyUrl}/validate/contact?contactID=${transaction.contactID}`,
          {
            method: 'GET',
            headers: {
              accept: '*/*',
              'content-type': 'application/json',
              Authorization: await getAuthToken(),
            },
          },
        );

        if (!contactResponse.ok) {
          console.error('Contact network response was not ok');
          return;
        }

        const responseData = await contactResponse.json();
        const contactData = responseData.contactInformation;

        if (contactData) {
          const filteredContactData = {
            ...contactData,
            emails:
              contactData.emails?.filter((email: { deleted?: boolean }) => !email?.deleted) || [],
            addresses:
              contactData.addresses?.filter(
                (address: { deleted?: boolean }) => !address?.deleted,
              ) || [],
            phones:
              contactData.phones?.filter((phone: { deleted?: boolean }) => !phone?.deleted) || [],
          };

          setSelectedContact(filteredContactData || null);

          // Find matching email index
          let emailIdx = 0;
          if (
            transaction?.email &&
            transaction.email !== '-' &&
            filteredContactData.emails?.length > 0
          ) {
            const matchingEmailIndex = filteredContactData.emails.findIndex(
              (email: { value?: string }) => email.value === transaction.email,
            );
            emailIdx =
              matchingEmailIndex !== -1
                ? matchingEmailIndex
                : (filteredContactData.emails?.findIndex(
                    (email: { is_primary?: boolean }) => email.is_primary,
                  ) ?? 0);
          } else {
            emailIdx =
              filteredContactData.emails?.findIndex(
                (email: { is_primary?: boolean }) => email.is_primary,
              ) ?? 0;
          }
          const selectedEmail = filteredContactData.emails?.[emailIdx]?.value || '';

          // Find matching phone index
          let phoneIdx = 0;
          if (transaction?.phone && filteredContactData.phones?.length > 0) {
            const matchingPhoneIndex = filteredContactData.phones.findIndex(
              (phone: { value?: string }) => phone.value === transaction.phone,
            );
            phoneIdx =
              matchingPhoneIndex !== -1
                ? matchingPhoneIndex
                : (filteredContactData.phones?.findIndex(
                    (phone: { is_primary?: boolean }) => phone.is_primary,
                  ) ?? 0);
          } else {
            phoneIdx =
              filteredContactData.phones?.findIndex(
                (phone: { is_primary?: boolean }) => phone.is_primary,
              ) ?? 0;
          }
          const selectedPhone = filteredContactData.phones?.[phoneIdx]?.value || '';

          // Find matching address index
          let addressIdx = 0;
          if (
            (transaction?.address ||
              transaction?.address2 ||
              transaction?.city ||
              transaction?.region ||
              transaction?.zip ||
              transaction?.country) &&
            filteredContactData.addresses?.length > 0
          ) {
            const transactionAddress = [
              transaction.address,
              transaction.address2,
              transaction.city,
              transaction.region,
              transaction.zip,
              transaction.country,
            ]
              .filter(Boolean)
              .join(', ')
              .toLowerCase()
              .trim();

            const matchingAddressIndex = filteredContactData.addresses.findIndex(
              (addr: {
                line1?: string;
                line2?: string;
                city?: string;
                region?: string;
                postal?: string;
                country?: string;
              }) => {
                const contactAddress = [
                  addr.line1,
                  addr.line2,
                  addr.city,
                  addr.region,
                  addr.postal,
                  addr.country,
                ]
                  .filter(Boolean)
                  .join(', ')
                  .toLowerCase()
                  .trim();

                return contactAddress === transactionAddress;
              },
            );

            addressIdx =
              matchingAddressIndex !== -1
                ? matchingAddressIndex
                : (filteredContactData.addresses?.findIndex(
                    (addr: { is_primary?: boolean }) => addr.is_primary,
                  ) ?? 0);
          } else {
            addressIdx =
              filteredContactData.addresses?.findIndex(
                (addr: { is_primary?: boolean }) => addr.is_primary,
              ) ?? 0;
          }
          const selectedAddress =
            filteredContactData.addresses?.[addressIdx] ||
            filteredContactData.addresses?.find(
              (addr: { is_primary?: boolean }) => addr.is_primary,
            ) ||
            filteredContactData.addresses?.[0] ||
            {};

          // Pre-populate formData with contact information
          setFormData((prevData) => ({
            ...prevData,
            contactID: filteredContactData?.id || '',
            firstName: filteredContactData?.firstName || '',
            lastName: filteredContactData?.lastName || '',
            email: selectedEmail,
            phone: selectedPhone,
            address1: selectedAddress.line1,
            address2: selectedAddress.line2,
            city: selectedAddress.city,
            region: selectedAddress.region,
            zip: selectedAddress.postal,
            country: selectedAddress.country,
            organizationName: filteredContactData?.organizationName || '',
          }));
        }
      } catch (error) {
        console.error('Error fetching contact:', error);
      }
    };

    fetchContact();
  }, [
    props.open,
    isEditMode,
    props.transaction?.contactID,
    props.merchant?.merchantName,
    selectedContact?.id,
  ]);

  // Pre-populate form data when in edit mode OR reset when opening in create mode
  useEffect(() => {
    if (!props.open) return; // Only run when drawer opens

    // Reset form data when opening in create mode (not edit mode)
    if (!isEditMode) {
      setFormData({
        ...initialFormData,
        formToFormDataMap: formData.formToFormDataMap || {},
      });
      setIsDisabled(false);
      setInvalidFields(new Set());
      setCurrentStep('form_information');
      setSelectedContact(null);
      setIsNewContact(false);
      return;
    }

    if (isEditMode && props.transaction) {
      const transaction = props.transaction;

      // Determine payment type based on payment method
      // Record-aware: a PayPal/Venmo gift processed online is an online gift.
      const getPaymentType = (paymentMethod: string): string => {
        return isOfflinePaymentRecord(transaction)
          ? formatOfflinePaymentMethod(paymentMethod)
          : 'Online';
      };

      // Convert custom inputs
      const convertCustomInputs = (customInputs: CustomInput[] | undefined): CustomInput[] => {
        if (!Array.isArray(customInputs)) return [];

        return customInputs.map((input) => ({
          ...input,
          id: input.id,
          value: input.answers?.[0] || '',
          title: input.title || input.label || '',
          options: input.options || [],
          type: input.type,
          required: input.required,
          inputType: input.inputType,
          global: input.global,
        }));
      };

      // Convert transaction tickets to EventLayout format
      // Transaction tickets are flat (each ticket is an attendee), but EventLayout expects
      // grouped tickets with nested attendees array
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const convertTickets = (transactionTickets: any[]): Ticket[] => {
        if (!Array.isArray(transactionTickets) || transactionTickets.length === 0) {
          return [
            {
              event: transaction.formName || '',
              ticketType: '',
              ticketID: '',
              ticketPrice: 0,
              seats: 1,
              quantity: 1,
              attendees: [],
            },
          ];
        }

        // Keep v2 purchased groups separate; legacy transactions still group by ticket type.
        const ticketGroups = new Map<
          string,
          {
            ticketID: string;
            ticketName: string;
            price: number;
            seats: number;
            type: string;
            purchasedGroupID?: string;
            groupSizeMode?: 'FIXED' | 'MINIMUM';
            minimumSeats?: number;
            maximumSeats?: number | null;
            pricingMode?: 'FLAT' | 'PER_SEAT';
            purchaseSeatCount?: number;
            unitPriceMinor?: number;
            subtotalAmountMinor?: number;
            discountAmountMinor?: number;
            totalAmountMinor?: number;
            currency?: string;
            promoCode?: string;
            ticketInventoryReleased?: boolean;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            attendees: any[];
          }
        >();

        transactionTickets.forEach((ticket) => {
          const ticketID = ticket.ticketID || '';
          const groupKey =
            transaction.ticketContractVersion === 2 && ticket.purchasedGroupID
              ? ticket.purchasedGroupID
              : ticketID;
          const purchasedGroup = transaction.purchasedGroups?.find(
            (group) => group.purchasedGroupID === ticket.purchasedGroupID,
          );
          if (!ticketGroups.has(groupKey)) {
            ticketGroups.set(groupKey, {
              ticketID: ticketID,
              ticketName: ticket.ticketName || '',
              price: ticket.price || 0,
              seats: purchasedGroup?.seatCount || ticket.seats || 1,
              type: ticket.type || 'SINGLE',
              purchasedGroupID: ticket.purchasedGroupID,
              ticketInventoryReleased: purchasedGroup?.ticketInventoryReleased,
              groupSizeMode: purchasedGroup?.groupSizeMode,
              minimumSeats: purchasedGroup?.minimumSeats,
              maximumSeats: purchasedGroup?.maximumSeats,
              pricingMode: purchasedGroup?.pricingMode,
              purchaseSeatCount: purchasedGroup?.seatCount,
              unitPriceMinor: purchasedGroup?.unitPriceMinor,
              subtotalAmountMinor: purchasedGroup?.subtotalAmountMinor,
              discountAmountMinor: purchasedGroup?.discountAmountMinor,
              totalAmountMinor: purchasedGroup?.totalAmountMinor,
              currency: purchasedGroup?.currency,
              promoCode: purchasedGroup?.promoCode,
              attendees: [],
            });
          }

          // Add this ticket as an attendee
          const group = ticketGroups.get(groupKey)!;
          group.attendees.push({
            firstName: ticket.firstName || '',
            lastName: ticket.lastName || '',
            email: ticket.email || '',
            customInputs: (ticket.customInputs || []).map((input: CustomInput) => ({
              ...input,
              value: input.answers?.[0] || '',
            })),
            confirmationID: ticket.confirmationID,
            checkInStatus: ticket.checkInStatus,
            purchasedGroupID: ticket.purchasedGroupID,
            seatIndex: ticket.seatIndex,
            ticketInventoryReleaseReasons: ticket.ticketInventoryReleaseReasons,
            pendingRefundRequestID: ticket.pendingRefundRequestID,
          });
        });

        // Convert groups to Ticket array format
        return Array.from(ticketGroups.values()).map((group) => ({
          event: transaction.formName || '',
          ticketType: group.ticketName,
          ticketID: group.ticketID,
          ticketPrice: group.price,
          seats: group.seats,
          quantity: group.type === 'GROUP' ? 1 : group.attendees.length,
          purchasedGroupID: group.purchasedGroupID,
          ticketInventoryReleased: group.ticketInventoryReleased,
          groupSizeMode: group.groupSizeMode,
          minimumSeats: group.minimumSeats,
          maximumSeats: group.maximumSeats,
          pricingMode: group.pricingMode,
          purchaseSeatCount: group.purchaseSeatCount,
          unitPriceMinor: group.unitPriceMinor,
          subtotalAmountMinor: group.subtotalAmountMinor,
          discountAmountMinor: group.discountAmountMinor,
          totalAmountMinor: group.totalAmountMinor,
          currency: group.currency,
          promoCode: group.promoCode,
          attendees: group.attendees,
        }));
      };

      const newFormData: EventFormData = {
        amount: parseFloat(String(transaction.grossAmount)) || 0,
        fees: parseFloat(String(transaction.fees)) || 0,
        customFee: parseFloat(String(transaction.fees)) || 0,
        feeCovered: transaction.coverFees === 'Yes' || transaction.coverFees === true,
        paymentMethod: transaction.paymentMethod === 'PAY_BY_BANK' ? 'Bank' : 'Card',
        accountNumber: transaction.accountNumber || '',
        routingNumber: transaction.routingNumber || '',
        accountType: (transaction.bankAccountType as 'CHECKING' | 'SAVINGS') || 'CHECKING',
        anonymous: transaction.anonymous || false,
        cardToken: null,
        email: transaction.email && transaction.email !== '-' ? transaction.email : '',
        firstName: transaction.name ? transaction.name.split(' ')[0] : '',
        lastName: transaction.name ? transaction.name.split(' ').slice(1).join(' ') : '',
        phone: transaction.phone || '',
        organizationName: transaction.organizationName || '',
        address1: transaction.address || '',
        address2: transaction.address2 || '',
        city: transaction.city || '',
        region: transaction.region || '',
        zip: transaction.zip || '',
        country: transaction.country || 'United States',
        paymentType: getPaymentType(transaction.paymentMethod),
        timestamp: transaction.timestamp || '',
        funds: [], // Events use tickets, not funds
        globalCustomInputs: convertCustomInputs(
          transaction.customInputs?.filter((input) => input.global) || [],
        ),
        purchaserCustomInputs: convertCustomInputs(
          transaction.customInputs?.filter((input) => input.inputType === 'purchaser') || [],
        ),
        tickets: convertTickets(transaction.tickets || []),
        attendees: [], // Legacy - attendees are now nested in tickets
        selectedEvent: transaction.formName || '',
        selectedEventData: null, // Will be populated by EventLayout when form loads
        formName: transaction.formName || '',
        promoCode: '', // Clear input field
        // If transaction had a promo code applied, reconstruct the appliedPromoCodes array
        appliedPromoCodes:
          transaction.promoCode && transaction.discount && transaction.discount > 0
            ? [
                {
                  code: transaction.promoCode,
                  discount: transaction.discount as number,
                  // We don't have the original discountType/discountValue from transaction,
                  // so we'll mark it as FIXED with the actual discount amount
                  discountType: 'FIXED' as const,
                  discountValue: transaction.discount as number,
                  applicableTickets:
                    transaction.tickets?.map((t: { ticketID?: string }) => t.ticketID || '') || [],
                },
              ]
            : [],
        totalPromoDiscount: transaction.discount ?? 0,
        originalTicketTotal: 0,
        // Tax deductible - default to true if not set
        taxDeductible: transaction.taxDeductible !== false,
        // Communication consent
        communicationConsent: transaction.communicationConsent || false,
      };

      setFormData(newFormData);
    }
  }, [isEditMode, props.transaction, props.open]);

  // Helper for consistent number parsing
  const safeParseFloat = (value: string | number | undefined): number => {
    const parsed = parseFloat(String(value));
    return isNaN(parsed) ? 0 : parsed;
  };
  const existingCheckoutBumpAmount = isEditMode
    ? Math.max(0, safeParseFloat(props.transaction?.checkoutBumpAmount))
    : 0;

  // Calculate fees based on the original amount.
  // This calculation must match checkout and backend plan-specific fee policy.
  const calculateFees = useCallback(
    (
      amount: number,
      merchant: CreateEventPaymentDrawerProps['merchant'],
      paymentMethodArg: string,
    ): number => {
      return calculateDashboardPaymentFees({
        amount,
        merchant,
        paymentMethod: paymentMethodArg,
        paymentProcessor: paymentMethodArg === 'Bank' ? 'FINIX' : undefined,
        coverFees: true,
        isEditOfExistingGift: isEditMode,
        subscription: props.linkedSubscription,
        // An edit keeps the gift's stored fee; nothing is re-priced (U1).
        existingGift: isEditMode ? props.transaction : undefined,
      });
    },
    [isEditMode, props.linkedSubscription, props.transaction],
  );

  // Calculate total with correct order of operations
  const calculateTotal = useCallback(
    (baseAmount: number, feeAmount: number, isFeeCovered: boolean): number => {
      const base = safeParseFloat(baseAmount);
      const feesAmount = safeParseFloat(feeAmount);

      // Then add fees if they should be covered
      const total = base + (isFeeCovered ? feesAmount : 0);

      return Number(total.toFixed(2));
    },
    [],
  );

  // Update the main effect - Event-only calculation
  useEffect(() => {
    if (!props.merchant) {
      setFormData((prev) => ({
        ...prev,
        fees: 0,
        ticketTotal: 0,
      }));
      return;
    }

    // Calculate total from tickets (for GROUP tickets, price is per unit not per attendee)
    const ticketTotal =
      formData.tickets?.reduce((total, ticket) => {
        return total + calculateDashboardTicketRowSubtotal(ticket, formData.selectedEventData);
      }, 0) || 0;

    // Apply promo discount (sum of all applied promo codes)
    let baseAmount = ticketTotal - (formData.totalPromoDiscount || 0);

    // Ensure amount doesn't go negative
    baseAmount = Math.max(0, baseAmount);

    // In edit mode, preserve the original fees from the transaction
    if (isEditMode && props.transaction) {
      const originalFees = parseFloat(
        String(
          props.transaction.coverFees === 'Yes' || props.transaction.coverFees === true
            ? props.transaction.fees
            : 0,
        ),
      );

      const newTotal = calculateTotal(baseAmount, originalFees, formData.feeCovered);
      setFormData((prev) => ({
        ...prev,
        fees: originalFees,
        ticketTotal: isNaN(newTotal) ? 0 : newTotal,
      }));
    } else {
      // Calculate fees based on the original amount for new transactions
      const calculatedFees = calculateFees(baseAmount, props.merchant, formData.paymentMethod);

      const newTotal = calculateTotal(baseAmount, calculatedFees, formData.feeCovered);
      setFormData((prev) => ({
        ...prev,
        fees: calculatedFees,
        ticketTotal: isNaN(newTotal) ? 0 : newTotal,
      }));
    }
  }, [
    formData.tickets,
    formData.totalPromoDiscount,
    formData.feeCovered,
    formData.paymentMethod,
    props.merchant,
    isEditMode,
    props.transaction,
    calculateFees,
    calculateTotal,
  ]);

  // Helper to validate a specific step and return invalid fields
  const validateStep = (step: FormStep): Set<string> => {
    const stepInvalidFields = new Set<string>();

    switch (step) {
      case 'form_information': {
        // Event validation
        if (!formData.selectedEvent) {
          stepInvalidFields.add('form_information-selectedEvent');
        }

        // Validate tickets and nested attendees
        const hasTicketsWithAttendees = formData.tickets?.some(
          (ticket) => ticket.ticketType && ticket.attendees?.length > 0,
        );

        if (!hasTicketsWithAttendees) {
          stepInvalidFields.add('form_information-tickets-empty');
        } else {
          // Validate each ticket and its nested attendees
          formData.tickets?.forEach((ticket, ticketIndex) => {
            if (!ticket.ticketType) {
              stepInvalidFields.add(`form_information-ticket-type-${ticketIndex}`);
            }

            ticket.attendees?.forEach((attendee, attendeeIndex) => {
              const fieldPrefix = `form_information-ticket-${ticketIndex}-attendee-${attendeeIndex}`;

              if (!attendee.firstName?.trim()) {
                stepInvalidFields.add(`${fieldPrefix}-firstName`);
              }
              if (!attendee.lastName?.trim()) {
                stepInvalidFields.add(`${fieldPrefix}-lastName`);
              }
              if (!attendee.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(attendee.email)) {
                stepInvalidFields.add(`${fieldPrefix}-email`);
              }

              // Validate custom inputs
              attendee.customInputs?.forEach((input, inputIndex) => {
                if (input.required && (!input.value || input.value.trim() === '')) {
                  stepInvalidFields.add(`${fieldPrefix}-custom-${inputIndex}`);
                }
              });
            });
          });
        }
        break;
      }

      case 'custom_input_information':
        // Validate global (merchant) custom inputs
        if (formData.globalCustomInputs) {
          formData.globalCustomInputs.forEach((input, index) => {
            if (input.required && (!input.value || input.value.trim() === '')) {
              stepInvalidFields.add(`custom_input_information-global-custom-${index}`);
            }
          });
        }
        // Validate purchaser (event) custom inputs
        if (formData.purchaserCustomInputs) {
          formData.purchaserCustomInputs.forEach((input, index) => {
            if (input.required && (!input.value || input.value.trim() === '')) {
              stepInvalidFields.add(`custom_input_information-purchaser-custom-${index}`);
            }
          });
        }
        break;

      case 'contact_information': {
        // Validate contact selection - must have either an existing contact selected OR be creating a new one
        // In edit mode with a contactID, the contact exists but may not be fetched yet
        // (ContactInformation only mounts when its step is active)
        const hasContactSelected =
          selectedContact !== null || isNewContact || (isEditMode && props.transaction?.contactID);
        if (!hasContactSelected) {
          stepInvalidFields.add('contact_information-contactSearch');
        }
        // Validate contact information fields (only if a contact context exists)
        if (hasContactSelected) {
          if (!formData.firstName?.trim()) {
            stepInvalidFields.add('contact_information-firstName');
          }
          if (!formData.lastName?.trim()) {
            stepInvalidFields.add('contact_information-lastName');
          }
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          stepInvalidFields.add('contact_information-email');
        }
        break;
      }

      case 'payment_information':
        if (formData.paymentType !== 'Online' && formData.feeCovered && !formData.customFee) {
          stepInvalidFields.add('payment_information-offline_custom_fee');
        }
        break;

      default:
        break;
    }

    return stepInvalidFields;
  };

  // Validate ALL steps and return combined invalid fields
  const validateAllSteps = (): Set<string> => {
    const allInvalidFields = new Set<string>();

    // Check if we should validate custom_input_information step
    const hasGlobalCustomInputs =
      (isEditMode && formData.globalCustomInputs && formData.globalCustomInputs.length > 0) ||
      (!isEditMode &&
        props.merchant?.globalCustomInputs &&
        props.merchant.globalCustomInputs.length > 0);
    const hasPurchaserCustomInputs =
      formData.purchaserCustomInputs && formData.purchaserCustomInputs.length > 0;
    const hasCustomInputs = hasGlobalCustomInputs || hasPurchaserCustomInputs;

    // Define steps to validate based on mode
    const stepsToValidate: FormStep[] = ['form_information'];
    if (hasCustomInputs) {
      stepsToValidate.push('custom_input_information');
    }
    stepsToValidate.push('contact_information');

    // Only validate payment step for non-online payments in create mode or offline edit mode
    if (!isEditMode || formData.paymentType !== 'Online') {
      stepsToValidate.push('payment_information');
    }

    // Validate each step
    stepsToValidate.forEach((step) => {
      const stepErrors = validateStep(step);
      stepErrors.forEach((error) => allInvalidFields.add(error));
    });

    return allInvalidFields;
  };

  // Update validateForm to check specific fund fields - Event-only validation
  // This is used by the Continue button to validate only the current step
  const handleContinue = () => {
    const stepErrors = validateStep(currentStep);

    // Convert to non-prefixed format for backward compatibility with field validation
    const newInvalidFields = new Set<string>();
    stepErrors.forEach((field) => {
      // Remove the step prefix for field-level validation
      const parts = field.split('-');
      if (
        parts.length > 1 &&
        [
          'form_information',
          'custom_input_information',
          'contact_information',
          'payment_information',
        ].includes(parts[0])
      ) {
        newInvalidFields.add(parts.slice(1).join('-'));
      } else {
        newInvalidFields.add(field);
      }
    });

    // Also keep prefixed versions for tab validation
    stepErrors.forEach((field) => newInvalidFields.add(field));

    if (newInvalidFields.size > 0) {
      setInvalidFields(newInvalidFields);

      // Add descriptive error message based on current step
      const stepErrorMessages = {
        form_information: 'Please complete all required event and attendee information',
        custom_input_information: 'Please complete all required custom fields',
        contact_information: 'Please complete all required contact information',
        payment_information: 'Please complete all required payment information',
      };

      toast.error(stepErrorMessages[currentStep] || 'Please complete all required fields');
    } else {
      // Clear any existing errors for current step
      setInvalidFields((prev) => {
        const updated = new Set(prev);
        Array.from(updated).forEach((field) => {
          if (field.startsWith(currentStep + '-')) {
            updated.delete(field);
          }
        });
        return updated;
      });

      // Check if we should skip the custom_input_information step
      // Include both global (merchant) custom inputs AND purchaser (event) custom inputs
      const hasGlobalCustomInputs =
        (isEditMode && formData.globalCustomInputs && formData.globalCustomInputs.length > 0) ||
        (!isEditMode &&
          props.merchant?.globalCustomInputs &&
          props.merchant.globalCustomInputs.length > 0);
      const hasPurchaserCustomInputs =
        formData.purchaserCustomInputs && formData.purchaserCustomInputs.length > 0;
      const hasCustomInputs = hasGlobalCustomInputs || hasPurchaserCustomInputs;

      const nextStepMap: Record<FormStep, FormStep> = {
        form_information: hasCustomInputs ? 'custom_input_information' : 'contact_information',
        custom_input_information: 'contact_information',
        contact_information: 'payment_information',
        payment_information: 'payment_information',
      };
      const nextStep = nextStepMap[currentStep];
      setCurrentStep(nextStep);
    }
  };

  // Handle Confirm Payment / Save Payment button - validates ALL steps
  const handleConfirmPayment = () => {
    const allErrors = validateAllSteps();

    if (allErrors.size > 0) {
      // Add both prefixed AND non-prefixed field names for validation
      // Prefixed names are used by stepHasErrors for section header red borders
      // Non-prefixed names are used by isFieldInvalid for individual input red borders
      const combinedErrors = new Set<string>();

      allErrors.forEach((field) => {
        // Add the prefixed version (for section headers)
        combinedErrors.add(field);

        // Also add the non-prefixed version (for individual input fields)
        const parts = field.split('-');
        if (
          parts.length > 1 &&
          [
            'form_information',
            'custom_input_information',
            'contact_information',
            'payment_information',
          ].includes(parts[0])
        ) {
          // Remove the step prefix and join the rest
          combinedErrors.add(parts.slice(1).join('-'));
        }
      });

      setInvalidFields(combinedErrors);
      toast.error('Please fix all validation errors before submitting');
      return;
    }

    // All validations passed - proceed with payment
    if (isEditMode) {
      savePayment();
    } else {
      sendToPaymentScreen();
    }
  };
  const handleBack = () => {
    // Check if we should skip the custom_input_information step
    // Include both global (merchant) custom inputs AND purchaser (event) custom inputs
    const hasGlobalCustomInputs =
      (isEditMode && formData.globalCustomInputs && formData.globalCustomInputs.length > 0) ||
      (!isEditMode &&
        props.merchant?.globalCustomInputs &&
        props.merchant.globalCustomInputs.length > 0);
    const hasPurchaserCustomInputs =
      formData.purchaserCustomInputs && formData.purchaserCustomInputs.length > 0;
    const hasCustomInputs = hasGlobalCustomInputs || hasPurchaserCustomInputs;

    switch (currentStep) {
      case 'custom_input_information':
        setCurrentStep('form_information');
        break;
      case 'contact_information':
        setCurrentStep(hasCustomInputs ? 'custom_input_information' : 'form_information');
        break;
      case 'payment_information':
        setCurrentStep('contact_information');
        break;
    }
  };

  // Helper function to check if a specific field is invalid
  const isFieldInvalid = (fieldId: string): boolean => {
    return invalidFields.has(fieldId);
  };

  // Update sendToPaymentScreen to handle submission state
  const sendToPaymentScreen = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    let stripeResult: { success: boolean; paymentMethod?: unknown; error?: unknown } | undefined =
      undefined;
    let finixCardErrors: string[] | undefined = undefined;
    let finixBankErrors: string[] | undefined = undefined;
    let stripePaymentMethod: unknown = undefined;

    // Calculate ticket total to detect free tickets (for GROUP tickets, price is per unit not per attendee)
    const ticketsTotal =
      formData.tickets?.reduce((total, ticket) => {
        const ticketPrice = parseFloat(String(ticket.ticketPrice || 0));
        const attendeesCount = ticket.attendees?.length || 0;
        const seats = ticket.seats || 1;
        // For GROUP tickets, numSold = attendeesCount / seats (price is for the group)
        const numSold = attendeesCount / seats;
        return total + ticketPrice * numSold;
      }, 0) || 0;
    let dashboardPurchase;
    try {
      dashboardPurchase = buildDashboardEventPurchase(
        formData.selectedEventData,
        formData.tickets || [],
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Ticket inventory is unavailable');
      setIsSubmitting(false);
      return;
    }
    const effectiveTicketsTotal =
      dashboardPurchase.ticketContractVersion === 2 ? dashboardPurchase.amount : ticketsTotal;

    // Apply promo discount to determine if this is a free ticket transaction
    const amountAfterDiscount = Math.max(
      0,
      effectiveTicketsTotal - (formData.totalPromoDiscount || 0),
    );

    // Check if this is a free ticket transaction (amount is 0 after discounts)
    const isFreeTickets = amountAfterDiscount === 0;

    // Payment processor validation
    // Free tickets bypass payment processing and go directly to offline donation flow
    // paymentType === 'Free' is set by EventLayout when all selected tickets have 0 price
    if (formData.paymentType !== 'Online' || isFreeTickets) {
      createOfflineDonation(isFreeTickets);
    } else {
      if (effectivePaymentProcessor == 'STRIPE' && formData.paymentMethod === 'Card') {
        if (stripeCardRef.current) {
          stripeResult = await stripeCardRef.current.handleSubmit();
          if (stripeResult.success) {
            stripePaymentMethod = stripeResult.paymentMethod;
          }
        }
      } else {
        if (formData.paymentMethod === 'Card' && finixCardRef.current) {
          finixCardErrors = await finixCardRef.current.checkIframeErrors();
        }
        if (formData.paymentMethod === 'Bank' && finixBankRef.current) {
          finixBankErrors = await finixBankRef.current.checkValidity();
        }
      }

      // Single validation check
      if (
        (stripeResult != undefined && !stripeResult.success) ||
        (finixCardErrors != undefined && finixCardErrors.length > 0) ||
        (finixBankErrors != undefined && finixBankErrors.length > 0)
      ) {
        // setInvalidFields(newInvalidFields);
        setIsSubmitting(false);
        return;
      }

      if (formData.paymentMethod === 'Card') {
        let tokenID = '';

        // Grab finix token if not stripe
        if (effectivePaymentProcessor !== 'STRIPE') {
          if (!form) {
            toast.error('Payment form not initialized');
            setIsSubmitting(false);
            return;
          }
          form.submit(
            import.meta.env.VITE_APP_FINIX_ENVIRONMENT,
            import.meta.env.VITE_APP_FINIX_APPLICATION_ID,
            async function (err, res) {
              if (err) {
                const parsedError = parsePaymentError(err, null, null, 'FINIX');
                toast.error(parsedError.userMessage);
                setIsSubmitting(false);
                return;
              }
              tokenID = res.data.id;
              createDonationBackend(tokenID, stripePaymentMethod, effectivePaymentProcessor);
            },
          );
        }
        // If stripe, create donation card
        else {
          createDonationBackend('', stripePaymentMethod, effectivePaymentProcessor);
        }
      } else if (formData.paymentMethod === 'Bank') {
        createDonationBackend('', {}, effectivePaymentProcessor);
      }
    }
  };

  // Add handler for clearing individual field errors
  const handleFieldFocus = (fieldName: string): void => {
    // Remove the field from invalidFields when it's focused
    setInvalidFields((prev) => {
      const newInvalidFields = new Set(prev);
      newInvalidFields.delete(fieldName);
      return newInvalidFields;
    });
  };

  const createOfflineDonation = async (isFreeTickets: boolean = false) => {
    // For events, create ticket data from nested attendees in tickets
    const tickets =
      formData.tickets?.flatMap((ticket) => {
        // Look up the ticket type from event data
        const eventTicket = formData.selectedEventData?.tickets?.find(
          (t: TicketInfo) => t.id === ticket.ticketID,
        );
        const ticketType = eventTicket?.type || 'SINGLE'; // Default to 'SINGLE' if not found

        return ticket.attendees?.map((attendee) => ({
          ticketID: ticket.ticketID,
          ticketName: ticket.ticketType,
          price: ticket.ticketPrice,
          type: ticketType, // Backend requires type field for validation (SINGLE or GROUP)
          seats: ticket.seats || ticket.attendees?.length || 1, // Backend requires seats for discount calculation
          firstName: attendee.firstName,
          lastName: attendee.lastName,
          email: attendee.email,
          checkInStatus: false,
          confirmationID: Math.random().toString(36).substring(2, 9),
          customInputs:
            attendee.customInputs?.map((input) => {
              const { value, ...inputWithoutValue } = input;
              return {
                ...inputWithoutValue,
                answers: [value],
                inputType: 'attendee',
              };
            }) || [],
        }));
      }) || [];

    // Build ticketMapping array - aggregates ticket sales by ticket type
    // This is required by the backend for promo code validation and inventory tracking
    const ticketMappingMapOffline: Record<
      string,
      { id: string; name: string; numSold: number; soldTotalPrice: number; quantity: number }
    > = {};
    formData.tickets?.forEach((ticket) => {
      if (ticket.ticketID) {
        const attendeesCount = ticket.attendees?.length || 0;
        const ticketPrice = parseFloat(String(ticket.ticketPrice || 0));
        const seats = ticket.seats || 1;

        // Calculate numSold: for GROUP tickets, numSold = attendeesCount / seats
        const numSold = attendeesCount / seats;
        // soldTotalPrice = numSold * ticketPrice (price is per unit, not per attendee)
        const soldTotalPrice = numSold * ticketPrice;

        if (ticketMappingMapOffline[ticket.ticketID]) {
          // Add to existing ticket type
          ticketMappingMapOffline[ticket.ticketID].quantity += attendeesCount;
          ticketMappingMapOffline[ticket.ticketID].numSold += numSold;
          ticketMappingMapOffline[ticket.ticketID].soldTotalPrice += soldTotalPrice;
        } else {
          // New ticket type
          ticketMappingMapOffline[ticket.ticketID] = {
            id: ticket.ticketID,
            name: ticket.ticketType || '',
            quantity: attendeesCount,
            numSold: numSold,
            soldTotalPrice: soldTotalPrice,
          };
        }
      }
    });
    const ticketMapping = Object.values(ticketMappingMapOffline);

    // Calculate total: for GROUP tickets, total = numSold * ticketPrice
    const ticketsTotal =
      formData.tickets?.reduce((total, ticket) => {
        const ticketPrice = parseFloat(String(ticket.ticketPrice || 0));
        const attendeesCount = ticket.attendees?.length || 0;
        const seats = ticket.seats || 1;
        const numSold = attendeesCount / seats;
        return total + ticketPrice * numSold;
      }, 0) || 0;
    const dashboardPurchase = buildDashboardEventPurchase(
      formData.selectedEventData,
      formData.tickets || [],
    );
    const effectiveTicketsTotal =
      dashboardPurchase.ticketContractVersion === 2 ? dashboardPurchase.amount : ticketsTotal;

    // Build top-level customInputs array (global + purchaser only)
    // NOTE: Attendee custom inputs are NOT included here - they are inside each ticket/attendee object
    // This matches the old CreateDonationSlideover behavior
    const customInputs = [
      // Global custom inputs (from merchant)
      ...(formData.globalCustomInputs?.map((input) => {
        const { value, ...inputWithoutValue } = input;
        return {
          ...inputWithoutValue,
          answers: [value],
          inputType: 'global',
          global: true,
        };
      }) || []),
      // Purchaser custom inputs (from event with questionType: PURCHASER)
      ...(formData.purchaserCustomInputs?.map((input) => {
        const { value, ...inputWithoutValue } = input;
        return {
          ...inputWithoutValue,
          answers: [value],
          inputType: 'purchaser',
        };
      }) || []),
    ];

    const paymentProcessor = effectivePaymentProcessor;

    // Build base request body
    const requestBody: Record<string, unknown> = {
      token: tokenID,
      contactID: formData.contactID,
      merchantName: props.merchant?.merchantName || '',
      defaultPaymentProcessor: paymentProcessor,
      stripeAccountID: props.merchant?.stripeAccountID ?? '',
      stripePaymentMethod: '',
      paymentMethod: isFreeTickets ? 'FREE' : 'OFFLINE',
      paymentType: isFreeTickets ? 'Online' : formData.paymentType,
      anonymous: formData.anonymous,
      amount: Math.max(0, effectiveTicketsTotal - (formData.totalPromoDiscount || 0)),
      customFee: formData.feeCovered ? parseFloat(String(formData.customFee || 0)) : 0,
      tip: 0,
      email: formData.email,
      uuid: String(uuid()),
      coverFees: formData.feeCovered,
      name: `${formData.firstName} ${formData.lastName}`,
      phone: formData.phone ?? '',
      address: formData.address1 ?? '',
      address2: formData.address2 ?? '',
      city: formData.city ?? '',
      region: formData.region ?? '',
      zip: formData.zip ?? '',
      country: formData.country ?? '',
      orgName: formData.organizationName,
      elementID: formData.selectedEventData?.elementID || '',
      formID: formData.selectedEventData?.elementID || '',
      formName: formData.selectedEvent || '',
      donationFrequency: 'ONE_TIME', // Event tickets are always one-time payments
      tickets: dashboardPurchase.ticketContractVersion === 2 ? dashboardPurchase.tickets : tickets,
      ticketMapping:
        dashboardPurchase.ticketContractVersion === 2
          ? dashboardPurchase.ticketMapping
          : ticketMapping,
      ...(dashboardPurchase.ticketContractVersion === 2
        ? {
            ticketContractVersion: 2,
            purchasedGroups: dashboardPurchase.purchasedGroups,
          }
        : {}),
      customInputs: customInputs,
      promoCode: formData.appliedPromoCodes?.[0]?.code || '', // Backend expects single promoCode string
      discount: formData.totalPromoDiscount || 0,
      timezone: getTimezone(props.merchant),
      timestamp: formData.timestamp,
      taxDeductible: formData.taxDeductible !== false,
      communicationConsent: formData.communicationConsent || false,
    };

    // Determine endpoint - use free transaction URL for free tickets
    let endpoint = `${
      import.meta.env.VITE_APP_DASHBOARD_TRANSACTIONS_URL
    }/checkout/create-donation`;

    // Switch to free ticket endpoint if amount is 0 (free tickets or 100% discount)
    if (isFreeTickets) {
      endpoint = import.meta.env.VITE_APP_FREE_TRANSACTION_URL;
      // Add free ticket specific fields - promoCode is already set above
      if ((formData.totalPromoDiscount || 0) > 0) {
        requestBody.fund = 'FREE';
      }
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(requestBody),
      });

      const responseBody = await response.json();

      if (!response.ok) {
        const parsedError = parsePaymentError(
          null,
          response,
          responseBody,
          effectivePaymentProcessor || 'STRIPE',
        );
        console.error('Payment error:', parsedError.code, parsedError.userMessage, responseBody);
        toast.error(parsedError.userMessage);
        setIsSubmitting(false);
        return;
      }

      // Success handling
      props.grabSelectableFunds?.();
      props.setOpen(false);
      clearStates();
      setIsSubmitting(false);

      if (props.onTransactionCreated && responseBody) {
        if (Array.isArray(responseBody)) {
          if (props.onMultipleTransactionsCreated) {
            props.onMultipleTransactionsCreated(responseBody);
          } else {
            responseBody.forEach((donation) => {
              props.onTransactionCreated?.(donation);
            });
          }
        } else {
          props.onTransactionCreated(responseBody);
        }
      }

      toast.success('Payment submitted successfully!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error(errorMessage);
      console.error('Payment processing error:', {
        processor: paymentProcessor,
        error: error,
        requestBody: requestBody,
      });
      setIsSubmitting(false);
    }
  };

  const createDonationBackend = async (
    tokenID: string,
    stripePaymentMethod: unknown,
    paymentProcessor: string | undefined,
  ): Promise<void> => {
    // For events, create ticket data from nested attendees in tickets
    const tickets =
      formData.tickets?.flatMap((ticket) => {
        // Look up the ticket type from event data
        const eventTicket = formData.selectedEventData?.tickets?.find(
          (t: TicketInfo) => t.id === ticket.ticketID,
        );
        const ticketType = eventTicket?.type || 'SINGLE'; // Default to 'SINGLE' if not found

        return ticket.attendees?.map((attendee) => ({
          ticketID: ticket.ticketID,
          ticketName: ticket.ticketType,
          price: ticket.ticketPrice,
          type: ticketType, // Backend requires type field for validation (SINGLE or GROUP)
          seats: ticket.seats || ticket.attendees?.length || 1, // Backend requires seats for discount calculation
          firstName: attendee.firstName,
          lastName: attendee.lastName,
          email: attendee.email,
          checkInStatus: false,
          confirmationID: Math.random().toString(36).substring(2, 9),
          customInputs:
            attendee.customInputs?.map((input) => {
              const { value, ...inputWithoutValue } = input;
              return {
                ...inputWithoutValue,
                answers: [value],
                inputType: 'attendee',
              };
            }) || [],
        }));
      }) || [];

    // Build ticketMapping array - aggregates ticket sales by ticket type
    // This is required by the backend for promo code validation and inventory tracking
    const ticketMappingMap: Record<
      string,
      { id: string; name: string; numSold: number; soldTotalPrice: number; quantity: number }
    > = {};
    formData.tickets?.forEach((ticket) => {
      if (ticket.ticketID) {
        const attendeesCount = ticket.attendees?.length || 0;
        const ticketPrice = parseFloat(String(ticket.ticketPrice || 0));
        const seats = ticket.seats || 1;

        // Calculate numSold: for GROUP tickets, numSold = attendeesCount / seats
        const numSold = attendeesCount / seats;
        // soldTotalPrice = numSold * ticketPrice (price is per unit, not per attendee)
        const soldTotalPrice = numSold * ticketPrice;

        if (ticketMappingMap[ticket.ticketID]) {
          // Add to existing ticket type
          ticketMappingMap[ticket.ticketID].quantity += attendeesCount;
          ticketMappingMap[ticket.ticketID].numSold += numSold;
          ticketMappingMap[ticket.ticketID].soldTotalPrice += soldTotalPrice;
        } else {
          // New ticket type
          ticketMappingMap[ticket.ticketID] = {
            id: ticket.ticketID,
            name: ticket.ticketType || '',
            quantity: attendeesCount,
            numSold: numSold,
            soldTotalPrice: soldTotalPrice,
          };
        }
      }
    });
    const ticketMapping = Object.values(ticketMappingMap);

    // Calculate total: for GROUP tickets, total = numSold * ticketPrice
    const ticketsTotal =
      formData.tickets?.reduce((total, ticket) => {
        const ticketPrice = parseFloat(String(ticket.ticketPrice || 0));
        const attendeesCount = ticket.attendees?.length || 0;
        const seats = ticket.seats || 1;
        const numSold = attendeesCount / seats;
        return total + ticketPrice * numSold;
      }, 0) || 0;
    const dashboardPurchase = buildDashboardEventPurchase(
      formData.selectedEventData,
      formData.tickets || [],
    );
    const effectiveTicketsTotal =
      dashboardPurchase.ticketContractVersion === 2 ? dashboardPurchase.amount : ticketsTotal;

    // Build top-level customInputs array (global + purchaser only)
    // NOTE: Attendee custom inputs are NOT included here - they are inside each ticket/attendee object
    // This matches the old CreateDonationSlideover behavior
    const customInputs = [
      // Global custom inputs (from merchant)
      ...(formData.globalCustomInputs?.map((input) => {
        const { value, ...inputWithoutValue } = input;
        return {
          ...inputWithoutValue,
          answers: [value],
          inputType: 'global',
          global: true,
        };
      }) || []),
      // Purchaser custom inputs (from event with questionType: PURCHASER)
      ...(formData.purchaserCustomInputs?.map((input) => {
        const { value, ...inputWithoutValue } = input;
        return {
          ...inputWithoutValue,
          answers: [value],
          inputType: 'purchaser',
        };
      }) || []),
    ];

    // Determine payment processor specific details
    const paymentDetails: Record<
      string,
      {
        paymentMethod: string;
        stripePaymentMethod?: unknown;
        finixToken?: string;
        processorType: string;
      }
    > = {
      STRIPE: {
        paymentMethod: 'CARD',
        stripePaymentMethod,
        processorType: 'STRIPE',
      },
      FINIX: {
        paymentMethod: 'CARD',
        finixToken: tokenID,
        processorType: 'FINIX',
      },
    };

    let processorDetails = paymentDetails[paymentProcessor || 'FINIX'];

    if (formData.paymentMethod === 'Bank') {
      paymentDetails['FINIX'] = {
        ...paymentDetails['FINIX'],
        paymentMethod: 'PAY_BY_BANK',
      };
      processorDetails = paymentDetails['FINIX'];
    }

    const requestBody = {
      ...processorDetails,
      token: tokenID,
      contactID: formData.contactID,
      accountNumber: formData.accountNumber ?? '',
      routingNumber: formData.routingNumber ?? '',
      accountType: formData.accountType ?? '',
      merchantName: props.merchant?.merchantName || '',
      defaultPaymentProcessor: processorDetails.processorType,
      stripeAccountID: props.merchant?.stripeAccountID ?? '',
      stripePaymentMethod: stripePaymentMethod ?? '',
      paymentMethod: processorDetails.paymentMethod,
      anonymous: formData.anonymous,
      amount: Math.max(0, effectiveTicketsTotal - (formData.totalPromoDiscount || 0)),
      fees: parseFloat(String(formData.fees || 0)),
      coverFees: formData.feeCovered,
      email: formData.email,
      uuid: String(uuid()),
      name: `${formData.firstName} ${formData.lastName}`,
      phone: formData.phone ?? '',
      address: formData.address1 ?? '',
      address2: formData.address2 ?? '',
      city: formData.city ?? '',
      region: formData.region ?? '',
      zip: formData.zip ?? '',
      country: formData.country ?? '',
      tip: 0,
      orgName: formData.organizationName,
      paymentType: formData.paymentType ?? '',
      elementID: formData.selectedEventData?.elementID || '',
      formID: formData.selectedEventData?.elementID || '',
      formName: formData.selectedEvent || '',
      donationFrequency: 'ONE_TIME', // Event tickets are always one-time payments
      tickets: dashboardPurchase.ticketContractVersion === 2 ? dashboardPurchase.tickets : tickets,
      ticketMapping:
        dashboardPurchase.ticketContractVersion === 2
          ? dashboardPurchase.ticketMapping
          : ticketMapping,
      ...(dashboardPurchase.ticketContractVersion === 2
        ? {
            ticketContractVersion: 2,
            purchasedGroups: dashboardPurchase.purchasedGroups,
          }
        : {}),
      customInputs: customInputs,
      promoCode: formData.appliedPromoCodes?.[0]?.code || '', // Backend expects single promoCode string
      discount: formData.totalPromoDiscount || 0,
      timezone: getTimezone(props.merchant),
      // TODO: Backend developer - include taxDeductible in payment creation payload
      // The formData.taxDeductible field should be sent to the backend
      // Default value should be true if not specified
      taxDeductible: formData.taxDeductible !== false,
      communicationConsent: formData.communicationConsent || false,
    };

    const endpoint = `${
      import.meta.env.VITE_APP_DASHBOARD_TRANSACTIONS_URL
    }/checkout/create-donation`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
          Authorization: await getAuthToken(),
        },
        body: JSON.stringify(requestBody),
      });

      const responseBody = await response.json();

      if (!response.ok) {
        setIsSubmitting(false);
        const parsedError = parsePaymentError(
          null,
          response,
          responseBody,
          effectivePaymentProcessor || 'STRIPE',
        );
        console.error('Payment error:', parsedError.code, parsedError.userMessage, responseBody);
        toast.error(parsedError.userMessage);
        return;
      }

      // Success handling
      props.grabSelectableFunds?.();
      props.setOpen(false);
      setIsSubmitting(false);
      clearStates();

      if (props.onTransactionCreated && responseBody && !responseBody.message) {
        if (Array.isArray(responseBody)) {
          if (props.onMultipleTransactionsCreated) {
            props.onMultipleTransactionsCreated(responseBody);
          } else {
            responseBody.forEach((donation) => {
              props.onTransactionCreated?.(donation);
            });
          }
        } else {
          props.onTransactionCreated(responseBody);
        }
      }

      const successMessage =
        paymentProcessor === 'FINIX'
          ? 'Finix payment processed successfully!'
          : 'Payment processed successfully!';
      toast.success(successMessage);
    } catch (error) {
      setIsSubmitting(false);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error(errorMessage);
      console.error('Payment processing error:', {
        processor: paymentProcessor,
        error: error,
        requestBody: requestBody,
      });
    }
  };

  const handleConfirmPaymentLatest = useLatestCallback(handleConfirmPayment);

  // Reset all form state to initial values
  const clearStates = () => {
    // Reset formData to initial state, preserving formToFormDataMap (API data)
    setFormData({
      ...initialFormData,
      formToFormDataMap: formData.formToFormDataMap || {},
    });

    // Reset local component state
    setIsDisabled(false);
    setInvalidFields(new Set<string>());
    setCurrentStep('form_information');
    // Reset promo UI state
    setIsPromoProcessing(false);
    setPromoMessage('Apply');
    // Reset contact selection state
    setSelectedContact(null);
    setIsNewContact(false);
    setForm(null);
    setTokenID('');
  };

  useEffect(() => {
    if (props.presentation !== 'workspace' || !props.setWorkspaceActions) return undefined;

    const actions: WorkspaceHeaderAction[] = [];

    if (isEditMode && canCancelEventAttendance(props.transaction)) {
      actions.push({
        key: 'cancel-attendance',
        label: 'Cancel attendance',
        variant: 'danger',
        disabled: isSubmitting,
        onClick: () => setShowCancelAttendanceModal(true),
      });
    }

    if (isEditMode && canSoftDeletePayment(props.transaction)) {
      actions.push({
        key: 'delete',
        label: 'Delete',
        variant: 'danger',
        disabled: isSubmitting,
        onClick: () => setShowDeleteModal(true),
      });
    }

    actions.push({
      key: 'confirm',
      label: isEditMode
        ? isSubmitting
          ? 'Saving...'
          : 'Save Payment'
        : isSubmitting
          ? 'Processing...'
          : 'Confirm Payment',
      variant: 'primary',
      disabled: isEditMode ? isSubmitting : isDisabled || isSubmitting,
      onClick: handleConfirmPaymentLatest,
    });

    props.setWorkspaceActions(actions);
    return () => props.setWorkspaceActions?.(null);
    // Register current button state without depending on recreated handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.presentation,
    props.setWorkspaceActions,
    props.transaction,
    isSubmitting,
    isDisabled,
    isEditMode,
    handleConfirmPaymentLatest,
  ]);

  // Add savePayment function for edit mode
  const savePayment = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const headers = editDonationHeaders(await getAuthToken());
      // Calculate total amount from tickets for event payments
      const ticketsTotal =
        formData.tickets?.reduce((total, ticket) => {
          const ticketPrice = parseFloat(String(ticket.ticketPrice || 0));
          const attendeesCount = ticket.attendees?.length || 0;
          const seats = ticket.seats || 1;
          const numSold = attendeesCount / seats;
          return total + ticketPrice * numSold;
        }, 0) || 0;

      // Build tickets array for backend - same format as createOfflineDonation/createDonationBackend
      const tickets =
        formData.tickets?.flatMap((ticket) => {
          // Look up the ticket type from event data
          const eventTicket = formData.selectedEventData?.tickets?.find(
            (t: TicketInfo) => t.id === ticket.ticketID,
          );
          const ticketType = eventTicket?.type || 'SINGLE'; // Default to 'SINGLE' if not found

          return ticket.attendees?.map((attendee) => ({
            ticketID: ticket.ticketID,
            ticketName: ticket.ticketType,
            price: ticket.ticketPrice,
            type: ticketType, // Backend requires type field for validation (SINGLE or GROUP)
            seats: ticket.seats || ticket.attendees?.length || 1, // Backend requires seats for discount calculation
            firstName: attendee.firstName,
            lastName: attendee.lastName,
            email: attendee.email,
            checkInStatus: attendee.checkInStatus || false,
            confirmationID: attendee.confirmationID || Math.random().toString(36).substring(2, 9),
            customInputs:
              attendee.customInputs?.map((input) => {
                const { value, ...inputWithoutValue } = input;
                return {
                  ...inputWithoutValue,
                  answers: [value],
                  inputType: 'attendee',
                };
              }) || [],
          }));
        }) || [];

      // Build ticketMapping array - aggregates ticket sales by ticket type
      const ticketMappingMap: Record<
        string,
        { id: string; name: string; numSold: number; soldTotalPrice: number; quantity: number }
      > = {};
      formData.tickets?.forEach((ticket) => {
        if (ticket.ticketID) {
          const attendeesCount = ticket.attendees?.length || 0;
          const ticketPrice = parseFloat(String(ticket.ticketPrice || 0));
          const seats = ticket.seats || 1;

          // Calculate numSold: for GROUP tickets, numSold = attendeesCount / seats
          const numSold = attendeesCount / seats;
          // soldTotalPrice = numSold * ticketPrice (price is per unit, not per attendee)
          const soldTotalPrice = numSold * ticketPrice;

          if (ticketMappingMap[ticket.ticketID]) {
            // Add to existing ticket type
            ticketMappingMap[ticket.ticketID].quantity += attendeesCount;
            ticketMappingMap[ticket.ticketID].numSold += numSold;
            ticketMappingMap[ticket.ticketID].soldTotalPrice += soldTotalPrice;
          } else {
            // New ticket type
            ticketMappingMap[ticket.ticketID] = {
              id: ticket.ticketID,
              name: ticket.ticketType || '',
              quantity: attendeesCount,
              numSold: numSold,
              soldTotalPrice: soldTotalPrice,
            };
          }
        }
      });
      const ticketMapping = Object.values(ticketMappingMap);
      const dashboardPurchase = buildDashboardEventPurchase(
        formData.selectedEventData,
        formData.tickets || [],
        buildDashboardInventoryCredits(props.transaction),
      );

      // Prepare the updated transaction data for the new Lambda function
      const updatedTransactionData = {
        contactID: formData.contactID,
        transactionID: props.transaction?.transactionID || '',
        name: `${formData.firstName} ${formData.lastName}`,
        organizationName: formData.organizationName,
        ...(formData.paymentType !== 'Online'
          ? {
              amount: Math.max(
                0,
                (dashboardPurchase.ticketContractVersion === 2
                  ? dashboardPurchase.amount
                  : ticketsTotal) - (formData.totalPromoDiscount || 0),
              ),
              fees: formData.customFee
                ? formData.feeCovered
                  ? formData.customFee
                  : 0
                : formData.feeCovered
                  ? formData.customFee
                  : 0,
              // The backend ignores `fees` on edit and prices a covered offline
              // fee from `customFee` (PE-2081), so send the operator's value there.
              ...(formData.feeCovered
                ? { customFee: parseFloat(String(formData.customFee || 0)) }
                : {}),
              coverFees: formData.feeCovered,
              timestamp: formData.timestamp,
            }
          : {}),
        email:
          formData.email && formData.email.trim() && formData.email !== '-' ? formData.email : '',
        phone: formData.phone || '',
        address: formData.address1 || '',
        address2: formData.address2 || '',
        city: formData.city || '',
        region: formData.region || '',
        zip: formData.zip || '',
        country: formData.country || '',
        paymentType: formData.paymentType,
        anonymous: formData.anonymous,
        // Include custom inputs (global + purchaser only - attendee inputs are in tickets)
        customInputs: [
          // Global custom inputs
          ...(formData.globalCustomInputs?.map((input) => ({
            ...input,
            answers: [input.value],
          })) || []),
          // Purchaser custom inputs
          ...(formData.purchaserCustomInputs?.map((input) => ({
            ...input,
            answers: [input.value],
          })) || []),
        ],
        // Include form/event name
        formName: formData.selectedEvent || '',
        formID: formData.selectedEventData?.elementID || '',
        // Include tickets and ticketMapping for event payments
        tickets:
          dashboardPurchase.ticketContractVersion === 2 ? dashboardPurchase.tickets : tickets,
        ticketMapping:
          dashboardPurchase.ticketContractVersion === 2
            ? dashboardPurchase.ticketMapping
            : ticketMapping,
        ...(dashboardPurchase.ticketContractVersion === 2
          ? {
              ticketContractVersion: 2,
              purchasedGroups: dashboardPurchase.purchasedGroups,
            }
          : {}),
        // TODO: Backend developer - include taxDeductible in payment update payload
        // PATCH /api/transactions/{transactionID} should accept taxDeductible field
        taxDeductible: formData.taxDeductible !== false,
        timezone: getTimezone(props.merchant),
        communicationConsent: formData.communicationConsent || false,
      };

      const endpoint = `${
        import.meta.env.VITE_APP_DASHBOARD_TRANSACTIONS_URL
      }/checkout/edit-donation`;

      // Call the new Lambda function endpoint
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updatedTransactionData),
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Handle specific error responses
        if (response.status === 404) {
          throw new Error('Donation not found');
        } else if (response.status === 403) {
          throw new Error("You don't have permission to edit this donation");
        } else if (response.status === 400) {
          const errorMessage =
            typeof responseData === 'string' ? responseData : JSON.stringify(responseData);
          throw new Error(errorMessage || 'Invalid request data');
        } else {
          const errorMessage =
            typeof responseData === 'string' ? responseData : JSON.stringify(responseData);
          throw new Error(errorMessage || 'Failed to update payment');
        }
      }

      // Success handling
      props.grabSelectableFunds?.();
      props.setOpen(false);
      clearStates();
      setIsSubmitting(false);

      // Update the donations table with the new data

      if (props.updateData) {
        // Pass the updated transaction data to updateData
        props.updateData(null, responseData.updatedDonation);
      }

      toast.success(responseData.message);

      // Log the response for debugging
    } catch (error) {
      console.error('Error updating payment:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update payment. Please try again.';
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  };

  // Delete payment handler (prototype - backend implementation needed)
  const handleDeletePayment = async () => {
    if (!props.transaction?.transactionID) {
      toast.error('Transaction ID not found');
      return;
    }
    if (!canSoftDeletePayment(props.transaction)) {
      toast.error('Cancel active Event attendance before deleting this payment');
      return;
    }
    setIsSubmitting(true);
    const deleted = await softDeletePayment(props.transaction.transactionID);
    setIsSubmitting(false);
    if (!deleted) {
      toast.error('Unable to delete payment');
      return;
    }
    toast.success('Payment deleted successfully');
    props.grabSelectableFunds?.();
    props.setOpen(false);
  };

  // Promo code validation - supports multiple promo codes
  // Promos are stored at the ticket level (ticket.promos) with fields: codeName, codeAmount, codeType
  const validatePromoCode = async (code: string): Promise<void> => {
    if (!code.trim()) return;

    setIsPromoProcessing(true);
    setPromoMessage('Validating...');

    try {
      const selectedEvent = formData.selectedEventData;

      // Check if code already applied
      if (formData.appliedPromoCodes.some((p) => p.code.toUpperCase() === code.toUpperCase())) {
        setPromoMessage('Already applied');
        setTimeout(() => setPromoMessage('Apply'), 3000);
        setIsPromoProcessing(false);
        return;
      }

      // Extract all promo codes from tickets (promos are stored at ticket level, not event level)
      // Each ticket can have its own promos array with fields: codeName, codeAmount, codeType
      const ticketPromos: Array<{
        codeName: string;
        codeAmount: string | number;
        codeType: string;
        ticketId: string;
        ticketName: string;
      }> = [];

      selectedEvent?.tickets?.forEach((ticket) => {
        if (ticket.promos && Array.isArray(ticket.promos)) {
          ticket.promos.forEach((promo) => {
            ticketPromos.push({
              codeName: promo.codeName,
              codeAmount: promo.codeAmount,
              codeType: promo.codeType,
              ticketId: ticket.id,
              ticketName: ticket.name,
            });
          });
        }
      });

      // Find matching promo code (case insensitive)
      const matchingPromos = ticketPromos.filter(
        (p) => p.codeName.toUpperCase() === code.toUpperCase(),
      );

      if (matchingPromos.length === 0) {
        setPromoMessage('Invalid code');
        setTimeout(() => setPromoMessage('Apply'), 3000);
        setIsPromoProcessing(false);
        return;
      }

      // Get the ticket IDs that this promo applies to
      const applicableTicketIds = matchingPromos.map((p) => p.ticketId);

      // Use the first matching promo for discount calculation (they should all have same discount)
      const promo = matchingPromos[0];
      const discountValue = parseFloat(String(promo.codeAmount)) || 0;
      const discountType = promo.codeType; // 'PERCENTAGE' or 'AMOUNT'/'FIXED'

      // Calculate discount based on selected tickets that are eligible for this promo
      // Must match backend logic: price per attendee = ticketPrice / seats
      let ticketTotal = 0;
      let discountAmount = 0;

      formData.tickets?.forEach((ticket) => {
        const ticketPrice = parseFloat(String(ticket.ticketPrice || 0));
        const attendeesCount = ticket.attendees?.length || 0;
        const seats = ticket.seats || 1;
        // For GROUP tickets, numSold = attendeesCount / seats
        const numSold = attendeesCount / seats;
        // Total for this ticket type = numSold * ticketPrice
        const ticketSubtotal =
          selectedEvent?.ticketContractVersion === 2
            ? calculateDashboardTicketRowSubtotal(ticket, selectedEvent)
            : numSold * ticketPrice;
        // Price per attendee = ticketPrice / seats (matches backend)
        const pricePerAttendee = ticketPrice / seats;

        // Check if this ticket is eligible for the promo
        const isEligible = ticket.ticketID ? applicableTicketIds.includes(ticket.ticketID) : false;

        if (isEligible) {
          ticketTotal += ticketSubtotal;

          // Calculate discount for this ticket (matches backend logic)
          if (selectedEvent?.ticketContractVersion === 2) {
            discountAmount +=
              discountType === 'PERCENTAGE'
                ? (ticketSubtotal * discountValue) / 100
                : Math.min(discountValue, ticketSubtotal);
          } else if (discountType === 'PERCENTAGE') {
            // Backend: discount = ticket_price * (promo_code_discount/100) per attendee
            discountAmount += ((pricePerAttendee * discountValue) / 100) * attendeesCount;
          } else {
            // AMOUNT or FIXED - backend: discount = promo_code_discount / seats per attendee
            const discountPerAttendee = discountValue / seats;
            discountAmount += Math.min(discountPerAttendee * attendeesCount, ticketSubtotal);
          }
        }
      });

      if (discountAmount === 0) {
        setPromoMessage('No eligible tickets');
        setTimeout(() => setPromoMessage('Apply'), 3000);
        setIsPromoProcessing(false);
        return;
      }

      // Add to array of applied promo codes
      const newPromoCode: {
        code: string;
        discount: number;
        discountType: 'PERCENTAGE' | 'FIXED';
        discountValue: number;
        applicableTickets: string[];
      } = {
        code: code,
        discount: discountAmount,
        discountType: discountType as 'PERCENTAGE' | 'FIXED',
        discountValue: discountValue,
        applicableTickets: applicableTicketIds,
      };

      setFormData((prev) => {
        const updatedAppliedCodes = [...prev.appliedPromoCodes, newPromoCode];
        const newTotalDiscount = updatedAppliedCodes.reduce((sum, p) => sum + p.discount, 0);

        return {
          ...prev,
          appliedPromoCodes: updatedAppliedCodes,
          totalPromoDiscount: newTotalDiscount,
          promoCode: '', // Clear input after applying
          originalTicketTotal: ticketTotal,
        };
      });

      // Hide input immediately after applying
      setPromoMessage('Apply');
      toast.success(`Promo code applied! You saved $${discountAmount.toFixed(2)}`);
    } catch (error) {
      console.error('Promo validation error:', error);
      setPromoMessage('Error');
      setTimeout(() => setPromoMessage('Apply'), 3000);
    } finally {
      setIsPromoProcessing(false);
    }
  };

  const removePromoCode = (codeToRemove: string): void => {
    setFormData((prev) => {
      const updatedAppliedCodes = prev.appliedPromoCodes.filter(
        (p) => p.code.toUpperCase() !== codeToRemove.toUpperCase(),
      );
      const newTotalDiscount = updatedAppliedCodes.reduce((sum, p) => sum + p.discount, 0);

      return {
        ...prev,
        appliedPromoCodes: updatedAppliedCodes,
        totalPromoDiscount: newTotalDiscount,
        originalTicketTotal: updatedAppliedCodes.length === 0 ? 0 : prev.originalTicketTotal,
      };
    });
    setPromoMessage('Apply');
  };

  const fetchFormElementAndTicketData = async () => {
    // Original API call code
    const response = await fetch(import.meta.env.VITE_APP_FORMS_URL, {
      method: 'GET',
      headers: {
        accept: '*/*',
        'content-type': 'application/json',
        Authorization: await getAuthToken(),
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    const formToFormDataMap: Record<string, EventData> = {};

    responseData.forEach((form: EventData) => {
      formToFormDataMap[form.formName] = {
        ...form,
        ticketContractVersion: resolveDashboardTicketContractVersion(form),
      };
    });

    setFormData((prev) => ({
      ...prev,
      formToFormDataMap: formToFormDataMap,
    }));
  };

  useEffect(() => {
    fetchFormElementAndTicketData();
  }, []);

  // REMOVED: useEffect that directly mutated props.merchant.defaultPaymentProcessor = 'FINIX'
  // This caused a render loop when defaultPaymentProcessor was absent (V2 orgs pre-verification).
  // Replaced by effectivePaymentProcessor derived constant at component top.

  useEffect(() => {
    setTimeout(() => {
      ReactTooltip.rebuild();
    }, 100);
    if (props.open) {
      fetchFormElementAndTicketData();
    }
  }, [props.open]);

  const handlePointerDownOutside = (event: {
    target: EventTarget | null;
    preventDefault: () => void;
  }): void => {
    // Check if the click is within any element with a class starting with 'mbx'
    const target = event.target as HTMLElement | null;
    if (target?.closest?.('[class^="mbx"]')) {
      event.preventDefault(); // Prevent the dialog from closing
    }
  };

  const workspaceStatus = isEditMode
    ? getRecordStatusInfo(props.transaction?.state)
    : { statusLabel: 'Draft', variant: 'neutral' as const };
  const workspaceContactName = isEditMode
    ? [formData.firstName, formData.lastName].filter(Boolean).join(' ').trim() ||
      formData.organizationName ||
      formData.email ||
      'Payment'
    : 'New Payment';
  const workspaceTicketTotal =
    formData.tickets?.reduce((total, ticket) => {
      return total + calculateDashboardTicketRowSubtotal(ticket, formData.selectedEventData);
    }, 0) || 0;
  const workspaceFees =
    formData.paymentType === 'Online'
      ? Number(formData.fees || 0)
      : Number(formData.customFee || 0);
  const workspaceAmount = `$${Math.max(
    0,
    workspaceTicketTotal -
      Number(formData.totalPromoDiscount || 0) +
      existingCheckoutBumpAmount +
      (formData.feeCovered ? workspaceFees : 0),
  ).toFixed(2)}`;
  const closeWorkspace = () => {
    props.setOpen(false);
    clearStates();
  };
  const workspaceHeaderActions = (
    <>
      {isEditMode && canCancelEventAttendance(props.transaction) && (
        <Button
          type="button"
          variant="danger"
          size="workspace"
          className={HEADER_ACTION_BUTTON_CLASS}
          onClick={() => setShowCancelAttendanceModal(true)}
          disabled={isSubmitting}
        >
          Cancel attendance
        </Button>
      )}
      {isEditMode && canSoftDeletePayment(props.transaction) && (
        <Button
          type="button"
          variant="danger"
          size="workspace"
          className={HEADER_ACTION_BUTTON_CLASS}
          onClick={() => setShowDeleteModal(true)}
          disabled={isSubmitting}
        >
          Delete
        </Button>
      )}
      <Button
        type="button"
        variant="primary"
        size="workspace"
        className={HEADER_ACTION_BUTTON_CLASS}
        onClick={handleConfirmPaymentLatest}
        disabled={isEditMode ? isSubmitting : isDisabled || isSubmitting}
      >
        {isEditMode
          ? isSubmitting
            ? 'Saving…'
            : 'Save changes'
          : isSubmitting
            ? 'Processing…'
            : 'Confirm payment'}
      </Button>
      <HeaderCloseAction
        label={isEditMode ? 'Close payment editor' : 'Close payment creator'}
        onClick={closeWorkspace}
        disabled={isSubmitting}
      />
    </>
  );

  return (
    <Sheet
      embedded={props.presentation === 'workspace'}
      open={props.open}
      onOpenChange={(open) => {
        if (!open) {
          clearStates();
        }
        props.setOpen(open);
      }}
    >
      <SheetContent
        embedded={props.presentation === 'workspace'}
        side="bottom"
        onPointerDownOutside={handlePointerDownOutside}
        className={
          props.presentation === 'workspace'
            ? 'h-full overflow-y-auto rounded-none bg-white p-0'
            : 'pointer-events-none h-[95vh] overflow-hidden rounded-t-[10px] bg-white'
        }
      >
        {/* Main Container - centered */}
        <div
          className={`mx-auto w-full ${
            props.presentation === 'workspace'
              ? 'min-h-full bg-white'
              : 'pointer-events-none h-full max-w-[1600px]'
          }`}
        >
          {props.presentation === 'workspace' && (
            <EntityIdentityHeader
              title={workspaceContactName}
              identifier={props.transaction?.transactionID}
              identifierLabel="Payment ID"
              onCopyIdentifier={(value: string) => navigator.clipboard.writeText(value)}
              metadata={
                isEditMode
                  ? [
                      { key: 'amount', value: workspaceAmount, emphasis: true },
                      { key: 'type', value: 'Event payment' },
                    ]
                  : [{ key: 'type', value: 'Event payment' }]
              }
              statusLabel={isEditMode ? workspaceStatus.statusLabel : undefined}
              statusVariant={isEditMode ? workspaceStatus.variant : undefined}
              actions={workspaceHeaderActions}
            />
          )}
          {/* Header with Close Button */}
          {props.presentation !== 'workspace' && (
            <div className="pointer-events-auto mb-4 flex items-center justify-between rounded-lg bg-white p-0">
              <div className="flex items-start space-x-3">
                <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[19px] border border-edge bg-paper-inset">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="size-6"
                  >
                    <path d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 0 1-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004ZM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 0 1-.921.42Z" />
                    <path
                      fillRule="evenodd"
                      d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v.816a3.836 3.836 0 0 0-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 0 1-.921-.421l-.879-.66a.75.75 0 0 0-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 0 0 1.5 0v-.81a4.124 4.124 0 0 0 1.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 0 0-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 0 0 .933-1.175l-.415-.33a3.836 3.836 0 0 0-1.719-.755V6Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-base text-ink-muted">
                    {isEditMode ? 'Edit Payment' : 'Create Payment'}
                  </p>
                  <p className="text-sm text-ink-muted">
                    {isEditMode
                      ? 'Modify payment details and contact information.'
                      : 'Set up and process a payment. Accept donations, sell event tickets, or log offline payments.'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Close Button */}
                <Button
                  variant="ghost"
                  onClick={() => {
                    props.setOpen(false);
                    clearStates();
                  }}
                >
                  Close
                </Button>

                {/* Save Button - Only in edit mode */}
                {isEditMode && (
                  <>
                    {canCancelEventAttendance(props.transaction) && (
                      <Button
                        onClick={() => setShowCancelAttendanceModal(true)}
                        disabled={isSubmitting}
                        variant="danger"
                        size="workspace"
                      >
                        Cancel attendance
                      </Button>
                    )}
                    {canSoftDeletePayment(props.transaction) && (
                      <Button
                        onClick={() => setShowDeleteModal(true)}
                        disabled={isSubmitting}
                        variant="danger"
                        size="workspace"
                      >
                        Delete
                      </Button>
                    )}
                    <Button
                      onClick={handleConfirmPayment}
                      disabled={isSubmitting}
                      variant="primary"
                      size="workspace"
                    >
                      {isSubmitting ? 'Saving...' : 'Save Payment'}
                    </Button>
                  </>
                )}

                {/* Confirm Payment Button - Always visible in create mode */}
                {!isEditMode && (
                  <Button
                    onClick={handleConfirmPayment}
                    disabled={isDisabled || isSubmitting}
                    variant="primary"
                    size="workspace"
                  >
                    {isSubmitting ? 'Processing...' : 'Confirm Payment'}
                  </Button>
                )}
              </div>
            </div>
          )}
          {props.presentation !== 'workspace' && (
            <div className="pb-3">
              <Separator className="bg-paper-inset" />
            </div>
          )}
          {/* Two Column Layout - Equal Width with Single Scroll */}
          <div
            className={`pointer-events-none grid grid-cols-1 ${
              props.presentation === 'workspace'
                ? 'items-start gap-4 px-3 py-4 md:px-5 xl:grid-cols-[minmax(0,1fr)_340px]'
                : 'h-[calc(100%-88px)] gap-6 overflow-y-auto lg:grid-cols-2 lg:overflow-y-hidden'
            }`}
          >
            {/* Left Panel - Event Summary - Fixed */}
            <div
              className={`pointer-events-auto relative flex flex-col gap-2 ${
                props.presentation === 'workspace' ? 'order-2' : ''
              }`}
            >
              <WorkspaceSectionCard
                title={isEditMode ? 'Updated payment' : 'Payment summary'}
                description="A live summary of the values that will be processed."
                icon={ReceiptPercentIcon}
                bodyClassName="space-y-3"
                enabled={props.presentation === 'workspace'}
              >
                {/* Event Summary */}
                <div
                  className={
                    props.presentation === 'workspace'
                      ? 'relative'
                      : 'relative rounded-lg border border-edge bg-paper-muted p-6'
                  }
                >
                  {/* Summary Content */}
                  <div className="space-y-4">
                    <div className="flex flex-col gap-3">
                      {formData.tickets?.map((ticket, ticketIndex) => {
                        const ticketPrice = Number.isInteger(ticket.unitPriceMinor)
                          ? Number(ticket.unitPriceMinor) / 100
                          : parseFloat(String(ticket.ticketPrice || 0));
                        const seats = ticket.seats || 1;
                        const attendeesCount = ticket.attendees?.length || 0;
                        const isGroupTicket = seats > 1;
                        // For GROUP tickets, numSold = attendeesCount / seats
                        const numSold = attendeesCount / seats;
                        const ticketSubtotal = calculateDashboardTicketRowSubtotal(
                          ticket,
                          formData.selectedEventData,
                        );

                        return (
                          <div key={ticketIndex} className="space-y-1">
                            {/* Ticket header with price */}
                            <div className="flex justify-between">
                              <span className="text-ink-secondary font-medium">
                                {ticket.ticketType || 'Ticket'}
                                {isGroupTicket && ` (${seats} seats)`}
                              </span>
                              <span className="text-base text-ink-secondary">
                                ${ticketPrice.toFixed(2)}
                                {ticket.pricingMode === 'PER_SEAT' ? ' per seat' : ''}
                                {isGroupTicket && numSold > 1 && ` × ${numSold}`}
                              </span>
                            </div>
                            {/* Attendee list */}
                            {ticket.attendees?.map((attendee, attendeeIndex) => (
                              <div key={`${ticketIndex}-${attendeeIndex}`} className="pl-3">
                                <div className="flex justify-between">
                                  <div className="flex flex-col space-y-[-2px]">
                                    <span className="text-ink-muted text-sm">
                                      {attendee.firstName} {attendee.lastName}
                                    </span>
                                    <div className="text-xs text-ink-muted">{attendee.email}</div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {/* Ticket subtotal for GROUP tickets with multiple units */}
                            {isGroupTicket &&
                              (numSold > 1 || ticket.pricingMode === 'PER_SEAT') && (
                                <div className="flex justify-end text-sm text-ink-muted">
                                  Subtotal: ${ticketSubtotal.toFixed(2)}
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Subtotal line (if promo applied) */}
                    {formData.appliedPromoCodes?.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-base text-ink-muted">Subtotal</span>
                        <span className="text-base text-ink-muted">
                          $
                          {(() => {
                            const total =
                              formData.tickets?.reduce((total, ticket) => {
                                return (
                                  total +
                                  calculateDashboardTicketRowSubtotal(
                                    ticket,
                                    formData.selectedEventData,
                                  )
                                );
                              }, 0) || 0;
                            return total.toFixed(2);
                          })()}
                        </span>
                      </div>
                    )}

                    {/* Discount line - show all applied promo codes */}
                    {formData.appliedPromoCodes?.length > 0 && (
                      <div className="space-y-1">
                        {formData.appliedPromoCodes.map((promo, index) => (
                          <div key={index} className="flex justify-between text-sage">
                            <span className="text-base">Promo ({promo.code})</span>
                            <span className="text-base">-${promo.discount?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {existingCheckoutBumpAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-base text-ink-muted">Additional support</span>
                        <span className="text-base text-ink-muted">
                          ${existingCheckoutBumpAmount.toFixed(2)}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between">
                      <span className="text-base text-ink-muted">Fee</span>
                      {formData.paymentType === 'Online' && formData.feeCovered ? (
                        <span className="text-base text-ink-muted">
                          {`$${parseFloat(String(formData.fees || 0)).toFixed(2)}`}
                        </span>
                      ) : formData.paymentType !== 'Online' && formData.feeCovered ? (
                        <span className="text-base text-ink-muted">
                          {`$${parseFloat(String(formData.customFee || 0)).toFixed(2)}`}
                        </span>
                      ) : (
                        <span className="text-base text-ink-muted">{`$0.00`}</span>
                      )}
                    </div>

                    <div className="border-t border-edge pt-4">
                      <div className="flex justify-between">
                        <span className="text-base text-ink-muted">Total</span>
                        <span className="text-base text-ink-muted">
                          $
                          {(() => {
                            const ticketsTotal =
                              formData.tickets?.reduce((total, ticket) => {
                                return (
                                  total +
                                  calculateDashboardTicketRowSubtotal(
                                    ticket,
                                    formData.selectedEventData,
                                  )
                                );
                              }, 0) || 0;

                            // Apply total promo discount (sum of all applied codes)
                            const afterDiscount = ticketsTotal - (formData.totalPromoDiscount || 0);

                            if (formData.paymentType === 'Online' && formData.feeCovered) {
                              return (
                                afterDiscount +
                                existingCheckoutBumpAmount +
                                parseFloat(String(formData.fees || 0))
                              ).toFixed(2);
                            } else if (formData.paymentType !== 'Online' && formData.feeCovered) {
                              return (
                                afterDiscount +
                                existingCheckoutBumpAmount +
                                parseFloat(String(formData.customFee || 0))
                              ).toFixed(2);
                            } else {
                              return (afterDiscount + existingCheckoutBumpAmount).toFixed(2);
                            }
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </WorkspaceSectionCard>
              {props.presentation === 'workspace' && (
                <TaxDeductibleCard
                  checked={formData.taxDeductible !== false}
                  onCheckedChange={(taxDeductible) =>
                    setFormData((previous) => ({ ...previous, taxDeductible }))
                  }
                />
              )}
            </div>

            {/* Right Panel - Form Fields - Only Scrollable Panel */}
            <div
              className={`pointer-events-auto min-w-0 rounded-lg bg-white ${
                props.presentation === 'workspace'
                  ? 'compact-editor-controls order-1'
                  : 'pr-0 [-ms-overflow-style:none] [scrollbar-width:none] lg:overflow-y-auto [&::-webkit-scrollbar]:hidden'
              }`}
            >
              <EventLayout
                presentation={props.presentation}
                stripeCardRef={stripeCardRef}
                finixCardRef={finixCardRef}
                finixBankRef={finixBankRef}
                formData={formData}
                setFormData={setFormData}
                handleFormDataChange={handleFormDataChange}
                invalidFields={invalidFields}
                setInvalidFields={setInvalidFields}
                isFieldInvalid={isFieldInvalid}
                handleFieldFocus={handleFieldFocus}
                merchant={props.merchant}
                form={form}
                setForm={setForm}
                setTokenID={setTokenID}
                isDrawerOpen={props.open}
                handleBack={handleBack}
                handleContinue={handleContinue}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                isEditMode={isEditMode}
                transaction={props.transaction}
                validatePromoCode={validatePromoCode}
                removePromoCode={removePromoCode}
                isPromoProcessing={isPromoProcessing}
                promoMessage={promoMessage}
                selectedContact={selectedContact}
                setSelectedContact={setSelectedContact}
                isNewContact={isNewContact}
                setIsNewContact={setIsNewContact}
              />
            </div>
          </div>
        </div>
      </SheetContent>

      {/* Delete Payment Modal */}
      {canSoftDeletePayment(props.transaction) && props.transaction && (
        <DeletePaymentModal
          open={showDeleteModal}
          setOpen={setShowDeleteModal}
          onConfirm={() => {
            handleDeletePayment();
            setShowDeleteModal(false);
          }}
          paymentDetails={{
            amount: parseFloat(String(props.transaction.grossAmount)) || 0,
            date: props.transaction.timestamp || '',
            paymentType: formData.paymentType || 'Offline',
            name: props.transaction.name || `${formData.firstName} ${formData.lastName}`,
          }}
        />
      )}
      {props.transaction && (
        <CancelEventAttendanceModal
          open={showCancelAttendanceModal}
          setOpen={setShowCancelAttendanceModal}
          payment={props.transaction}
          onSuccess={() => {
            props.grabSelectableFunds?.();
            props.setOpen(false);
          }}
        />
      )}
    </Sheet>
  );
}
